import { ChevronDown, ChevronUp, ExternalLink, Star } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { fmtInt, fmtSigned, signClass } from "@/lib/format";
import RetailSentiment from "@/components/cot/RetailSentiment";
import DivergenceCell from "@/components/cot/DivergenceCell";

const COLS = [
  { key: "symbol",                label: "SYM",        align: "left",  width: "w-20" },
  { key: "name",                  label: "MARKET",     align: "left",  width: "" },
  { key: "category",              label: "CAT",        align: "left",  width: "w-24 hidden md:table-cell" },
  { key: "_retail",               label: "RETAIL (NON-REPT)", align: "left", width: "w-44" },
  { key: "_divergence",           label: "SM vs RETAIL", align: "left", width: "w-36" },
  { key: "open_interest",         label: "OPEN INT",   align: "right" },
  { key: "change_open_interest",  label: "Δ OI",       align: "right" },
  { key: "noncomm_long",          label: "NC LONG",    align: "right" },
  { key: "noncomm_short",         label: "NC SHORT",   align: "right" },
  { key: "net_noncomm",           label: "NET NC",     align: "right" },
  { key: "cot_index_1y",          label: "COT IDX 1Y", align: "right", width: "w-32" },
  { key: "change_net_noncomm",    label: "Δ NET NC",   align: "right" },
  { key: "_detail",               label: "",           align: "right" },
];

// Williams COT Index 0-100 with a track + marker. High = crowded long (contrarian bearish).
function CotIndexCell({ idx, z, extreme }) {
  if (idx == null) {
    return <td className="py-1.5 px-2 text-right text-zinc-700">—</td>;
  }
  const dist = Math.abs(idx - 50);
  const color = extreme ? "#ef4444" : dist >= 35 ? "#f59e0b" : "#71717a";
  return (
    <td className="py-1.5 px-2 text-right" data-testid={`cotidx-cell`}>
      <div className="flex items-center justify-end gap-1.5">
        <div className="relative h-1.5 w-14 bg-zinc-800 rounded-[1px] overflow-hidden">
          <div className="absolute inset-y-0 left-1/2 w-px bg-zinc-600" />
          <div
            className="absolute top-1/2 h-2 w-2 -translate-y-1/2 -translate-x-1/2 rounded-full"
            style={{ left: `${idx}%`, backgroundColor: color }}
          />
        </div>
        <span className="tabular-nums text-[11px] w-7 text-right" style={{ color }}>
          {Math.round(idx)}
        </span>
        {z != null && (
          <span className="tabular-nums text-[11px] text-zinc-500 w-9 text-right">
            {z > 0 ? "+" : ""}{z}σ
          </span>
        )}
      </div>
    </td>
  );
}

export default function COTTable({ rows, sortKey, sortDir, onSort, selectedCode, onSelect, loading, usdTint, watched, onToggleWatch }) {
  const navigate = useNavigate();
  // FX pairs that move INVERSELY to USD (long EUR/USD = short USD)
  const INVERSE_USD = new Set(["EUR", "GBP", "JPY", "CHF", "CAD", "AUD", "NZD"]);
  const tintFor = (sym) => {
    if (!usdTint?.active) return "";
    let positive = null; // true => tint green, false => red
    if (sym === "DXY") positive = usdTint.bias === "bullish";
    else if (INVERSE_USD.has(sym)) positive = usdTint.bias === "bearish";
    if (positive === null) return "";
    return positive
      ? "border-l-4 border-l-emerald-500/80"
      : "border-l-4 border-l-red-500/80";
  };
  return (
    <div className="border border-zinc-800 bg-[#09090B] overflow-hidden" data-testid="cot-table-wrapper">
      <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest text-zinc-400">
          ▍ COT Data · Legacy Futures Only
        </div>
        <div className="text-[11px] uppercase tracking-widest text-zinc-600">
          click row to chart · click header to sort
        </div>
      </div>
      <div className="overflow-x-auto max-h-[640px]">
        <table className="w-full text-xs whitespace-nowrap" data-testid="cot-table">
          <thead className="sticky top-0 bg-zinc-950 z-10">
            <tr className="border-b border-zinc-700 text-zinc-400">
              {COLS.map((c) => {
                const active = sortKey === c.key;
                const hideCls = c.hideSm ? "hidden lg:table-cell" : "";
                const widthCls = c.width || "";
                const sortable = c.key !== "_detail" && c.key !== "_retail" && c.key !== "_divergence";
                return (
                  <th
                    key={c.key}
                    onClick={() => sortable && onSort(c.key)}
                    className={`py-2 px-2 font-normal uppercase tracking-wider select-none ${
                      sortable ? "cursor-pointer hover:text-zinc-100" : ""
                    } ${c.align === "right" ? "text-right" : "text-left"} ${hideCls} ${widthCls}`}
                    data-testid={`th-${c.key}`}
                  >
                    <span className={`inline-flex items-center gap-1 ${active ? "text-amber-term" : ""}`}>
                      {c.label}
                      {active &&
                        (sortDir === "asc" ? (
                          <ChevronUp size={11} strokeWidth={1.5} />
                        ) : (
                          <ChevronDown size={11} strokeWidth={1.5} />
                        ))}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={COLS.length} className="py-8 text-center text-zinc-500 text-xs uppercase tracking-widest">
                  Loading CFTC data…
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={COLS.length} className="py-8 text-center text-zinc-500 text-xs uppercase tracking-widest">
                  No markets match filter
                </td>
              </tr>
            )}
            {!loading &&
              rows.map((r) => {
                const selected = r.code === selectedCode;
                const aligned = r._alignment === "aligned";
                const contra = r._alignment === "contra";
                const rowCls = selected
                  ? "bg-amber-500/10"
                  : aligned
                  ? "bg-red-500/5 outline outline-1 -outline-offset-1 outline-red-500/40 hover:bg-red-500/10"
                  : contra
                  ? "bg-emerald-500/5 hover:bg-emerald-500/10"
                  : "hover:bg-zinc-900";
                return (
                  <tr
                    key={r.code}
                    onClick={() => onSelect(r.code)}
                    data-testid={`row-${r.symbol}`}
                    data-alignment={r._alignment || ""}
                    style={selected ? { boxShadow: "inset 3px 0 0 0 hsl(var(--primary))" } : undefined}
                    className={`border-b border-zinc-900 cursor-pointer ${rowCls} ${tintFor(r.symbol)}`}
                  >
                    <td className="py-1.5 px-2 font-semibold text-amber-term">
                      <span className="inline-flex items-center gap-1.5">
                        {onToggleWatch && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onToggleWatch(r);
                            }}
                            data-testid={`watch-${r.symbol}`}
                            title={watched?.has(r.code) ? "Remove from watchlist" : "Add to watchlist"}
                            className="text-zinc-600 hover:text-amber-term transition-colors"
                          >
                            <Star
                              size={12}
                              strokeWidth={1.5}
                              className={watched?.has(r.code) ? "fill-amber-term text-amber-term" : ""}
                            />
                          </button>
                        )}
                        {r.symbol}
                        {aligned && (
                          <span
                            data-testid={`badge-aligned-${r.symbol}`}
                            className="text-[11px] tracking-widest border border-red-500/60 text-neg px-1 leading-tight"
                          >
                            RO
                          </span>
                        )}
                        {contra && (
                          <span
                            data-testid={`badge-contra-${r.symbol}`}
                            className="text-[11px] tracking-widest border border-emerald-500/60 text-pos px-1 leading-tight"
                          >
                            RN
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="py-1.5 px-2 text-zinc-100">{r.name}</td>
                    <td className="py-1.5 px-2 text-zinc-500 uppercase text-[11px] tracking-widest hidden md:table-cell">
                      {r.category}
                    </td>
                    <td className="py-1.5 px-2">
                      <RetailSentiment
                        nonreptLong={r.nonrept_long}
                        nonreptShort={r.nonrept_short}
                        testid={`retail-${r.symbol}`}
                      />
                    </td>
                    <td className="py-1.5 px-2">
                      <DivergenceCell row={r} testid={`divergence-${r.symbol}`} />
                    </td>
                    <td className="py-1.5 px-2 text-right tabular-nums">{fmtInt(r.open_interest)}</td>
                    <td className={`py-1.5 px-2 text-right tabular-nums ${signClass(r.change_open_interest)}`}>
                      {fmtSigned(r.change_open_interest)}
                    </td>
                    <td className="py-1.5 px-2 text-right tabular-nums text-pos">{fmtInt(r.noncomm_long)}</td>
                    <td className="py-1.5 px-2 text-right tabular-nums text-neg">{fmtInt(r.noncomm_short)}</td>
                    <td className={`py-1.5 px-2 text-right tabular-nums font-semibold ${signClass(r.net_noncomm)}`}>
                      {fmtSigned(r.net_noncomm)}
                    </td>
                    <CotIndexCell idx={r.cot_index_1y} z={r.net_zscore} extreme={r.extreme} />
                    <td className={`py-1.5 px-2 text-right tabular-nums ${signClass(r.change_net_noncomm)}`}>
                      {fmtSigned(r.change_net_noncomm)}
                    </td>
                    <td className="py-1.5 px-2 text-right">
                      <button
                        data-testid={`detail-${r.symbol}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/market/${r.code}`);
                        }}
                        className="inline-flex items-center gap-1 border border-zinc-700 px-2 py-0.5 text-[11px] uppercase tracking-widest text-zinc-300 hover:border-zinc-400 hover:text-zinc-100 transition-colors duration-75"
                      >
                        VIEW <ExternalLink size={10} strokeWidth={1.5} />
                      </button>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
